import { type PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import type { DateOverride } from "../helpers/date-override"
import { createPanelSlice, type PanelRootState } from "./create-panel-slice"

export interface SelectedGroup {
  id: string
  name: string
  type: string
}

export interface KPIState {
  sectionSettings: Record<string, unknown>
  selectedGroup: SelectedGroup | null
  selectedTab: string
  // Per-panel date range that supersedes the dashboard's dates when present.
  dateOverride: DateOverride | null
  isPreferencesLoaded: boolean
  /** Transient (never persisted): set by clonePanelState so the duplicate's sync hook
   *  persists the copied state once, then cleared via clearPendingClonePersist. */
  pendingClonePersist?: boolean
}

/** Keyed by dashboardId at the top, panelId at the inner level — the default layout has two
 *  KPI panels (summary + decisions), so their state must not collide. */
export type KPIRootState = PanelRootState<KPIState>

/** Slice key + preference subtype used when a KPI panel renders without a PanelIdProvider. */
export const KPI_PREFERENCE_SUBTYPE_BASE = "kpi"

/** Persisted fields — single source for the setPreferences whitelist (here) and the sync hook's diff baseline. */
export const KPI_PREFERENCE_FIELDS = ["sectionSettings", "selectedGroup", "selectedTab", "dateOverride"] as const

/** Co-located access to a panel's raw slot (undefined when absent); callers coalesce to DEFAULT_KPI_STATE. */
export const selectKPIPanel = (
  state: ReduxState,
  dashboardId: string,
  panelKey: string,
): KPIState | undefined => state.dashboard.kpi[dashboardId]?.[panelKey]

export const DEFAULT_KPI_STATE: KPIState = {
  sectionSettings: {},
  selectedGroup: null,
  selectedTab: "decisions",
  dateOverride: null,
  isPreferencesLoaded: false,
}

export const kpiSlice = createPanelSlice({
  name: "dashboard/kpi",
  defaultState: DEFAULT_KPI_STATE,
  preferenceFields: KPI_PREFERENCE_FIELDS,
  // Spread sectionSettings: {} to avoid sharing the mutable default object between panels.
  freshOverrides: () => ({ sectionSettings: {} }),
  cloneOverrides: (sourceState) => ({
    sectionSettings: { ...sourceState.sectionSettings },
    selectedGroup: sourceState.selectedGroup ? { ...sourceState.selectedGroup } : null,
  }),
  reducers: ({ ensure, initInstance }) => ({
    setSectionExpanded(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, key: string, value: unknown }>,
    ) {
      const { dashboardId, panelId, key, value } = action.payload
      ensure(state, dashboardId, panelId).sectionSettings[key] = value
    },

    setSelectedGroup(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, group: SelectedGroup | null }>,
    ) {
      const { dashboardId, panelId, group } = action.payload
      ensure(state, dashboardId, panelId).selectedGroup = group
    },

    setSelectedTab(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, tab: string }>,
    ) {
      const { dashboardId, panelId, tab } = action.payload
      ensure(state, dashboardId, panelId).selectedTab = tab
    },

    setDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, dateOverride: DateOverride }>,
    ) {
      const { dashboardId, panelId, dateOverride } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = dateOverride
    },

    clearDateOverride(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      ensure(state, dashboardId, panelId).dateOverride = null
    },

    // Seed the module-config tab/dateOverride as the panel baseline. No-ops once the instance
    // exists, so a saved preference (or legacy fallback) that loads first is never overwritten —
    // this must run before the sync's fallback, which omits selectedTab/dateOverride so it can't
    // strand the panel here.
    initFromModuleConfig(
      state,
      action: PayloadAction<{ dashboardId: string, panelId: string, selectedTab?: string, dateOverride?: DateOverride | null }>,
    ) {
      const { dashboardId, panelId, selectedTab, dateOverride } = action.payload
      const overrides: Partial<KPIState> = {}
      if (selectedTab != null) overrides.selectedTab = selectedTab
      if (dateOverride !== undefined) overrides.dateOverride = dateOverride
      initInstance(state, dashboardId, panelId, overrides)
    },
  }),
})

export default kpiSlice.reducer
